import { useCallback, useState } from 'react';

import { getActivities } from '../api';
import type { ActivityFilter } from '../components/Activities/utils/activitiesFilters';

const PAGE_SIZE = 100;

/**
 * Custom hook for managing activities fetching with filtering
 * @param filter - Filter activities by type ('all', 'to-do', 'done', etc.)
 * @param showImportant - Filter to show only important activities
 * @param staffId - Filter activities by staff ID (optional, from StaffFilter for specific_staff selection)
 * @param currentUserId - Current user ID (for current_user selection)
 * @returns Fetch function for pagination and can_pull permission flag
 */
export const useActivitiesSearch = (
  filter: ActivityFilter,
  showImportant: 'all' | 'important',
  staffId: number | null,
  currentUserId: number | null,
) => {
  const [canPull, setCanPull] = useState(false);

  const fetchActivitiesPage = useCallback(
    async(page: number) => {
      // Map filter to API parameters
      let doneParam: boolean | undefined = undefined;

      if (filter === 'done') {
        doneParam = true;
      } else if (filter !== 'all') {
        // All time-based filters (to-do, overdue, today, tomorrow, this-week, next-week) are "not done"
        doneParam = false;
      }

      // Use staff_id for specific staff selection, user for current user selection
      // If both are null (Everyone selected), fetch all activities
      const response = await getActivities({
        page,
        page_size: PAGE_SIZE,
        staff_id: staffId ?? undefined,
        user: staffId ? undefined : (currentUserId ?? undefined),
        done: doneParam,
      });

      setCanPull(response.can_pull ?? true);

      return {
        results: response.results,
        next: response.next ?? null,
      };
    },
    [filter, staffId, currentUserId],
  );

  return {
    fetchActivitiesPage,
    canPull,
  };
};
